import React, { useState, useMemo } from 'react';
import { Clock } from 'lucide-react';
import { Card, CardHeader, ProgressBar } from '../shared';
import { useTheme } from '../../contexts/ThemeContext';
import { formatCurrency } from '../../utils/formatters';
import { retirementPlan } from '../../utils/calculators';

interface RetirementCalculatorProps {
  variant?: 'card' | 'page';
}

export const RetirementCalculator: React.FC<RetirementCalculatorProps> = ({ variant = 'card' }) => {
  const { darkMode } = useTheme();

  const [currentAgeStr, setCurrentAgeStr] = useState('');
  const [retirementAgeStr, setRetirementAgeStr] = useState('');
  const [currentSavingsStr, setCurrentSavingsStr] = useState('');
  const [monthlyContributionStr, setMonthlyContributionStr] = useState('');
  const [annualReturnStr, setAnnualReturnStr] = useState('');
  const [inflationStr, setInflationStr] = useState('');
  const [desiredIncomeStr, setDesiredIncomeStr] = useState('');

  const currentAge = Number(currentAgeStr) || 0;
  const retirementAge = Number(retirementAgeStr) || 0;
  const currentSavings = Number(currentSavingsStr) || 0;
  const monthlyContribution = Number(monthlyContributionStr) || 0;
  const annualReturn = Number(annualReturnStr) || 0;
  const inflation = Number(inflationStr) || 0;
  const desiredIncome = Number(desiredIncomeStr) || 0;

  const inputClass = `w-full px-3 py-2 rounded-lg text-sm ${
    darkMode
      ? 'bg-white/10 border border-white/10 text-white placeholder-slate-500'
      : 'bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400'
  }`;

  const labelClass = `text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`;

  const results = useMemo(
    () => retirementPlan({ currentAge, retirementAge, currentSavings, monthlyContribution, annualReturn, inflation, desiredIncome }),
    [currentAge, retirementAge, currentSavings, monthlyContribution, annualReturn, inflation, desiredIncome],
  );

  return (
    <Card variant={variant}>
      <CardHeader
        title="Retirement Calculator"
        icon={<Clock size={20} />}
        variant={variant}
      />
      <div className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div>
            <label className={labelClass}>Current Age</label>
            <input type="number" value={currentAgeStr} onChange={e => setCurrentAgeStr(e.target.value)} onFocus={e => e.target.select()} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Retire At</label>
            <input type="number" value={retirementAgeStr} onChange={e => setRetirementAgeStr(e.target.value)} onFocus={e => e.target.select()} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Current Savings</label>
            <input type="number" value={currentSavingsStr} onChange={e => setCurrentSavingsStr(e.target.value)} onFocus={e => e.target.select()} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Monthly Contribution</label>
            <input type="number" value={monthlyContributionStr} onChange={e => setMonthlyContributionStr(e.target.value)} onFocus={e => e.target.select()} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Annual Return (%)</label>
            <input type="number" value={annualReturnStr} step={0.5} onChange={e => setAnnualReturnStr(e.target.value)} onFocus={e => e.target.select()} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Inflation (%)</label>
            <input type="number" value={inflationStr} step={0.5} onChange={e => setInflationStr(e.target.value)} onFocus={e => e.target.select()} className={inputClass} />
          </div>
          <div className="col-span-2">
            <label className={labelClass}>Desired Annual Income</label>
            <input type="number" value={desiredIncomeStr} onChange={e => setDesiredIncomeStr(e.target.value)} onFocus={e => e.target.select()} className={inputClass} />
          </div>
        </div>

        {/* Results */}
        <div className={`rounded-xl p-4 space-y-3 ${darkMode ? 'bg-white/5' : 'bg-violet-50/50'}`}>
          <div className="text-center">
            <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Projected Retirement Savings</p>
            <p className={`text-2xl font-bold ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}>
              {formatCurrency(results.fv)}
            </p>
            <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              {formatCurrency(results.todaysDollars)} in today's dollars
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Monthly Income</p>
              <p className={`text-sm font-semibold ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                {formatCurrency(results.monthlyIncome)}
              </p>
            </div>
            <div>
              <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Years Funded</p>
              <p className={`text-sm font-semibold ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                {results.yearsFunded}
              </p>
            </div>
            <div>
              <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Status</p>
              <p className={`text-sm font-semibold ${
                !results.hasGoal
                  ? (darkMode ? 'text-slate-300' : 'text-slate-700')
                  : results.onTrack ? (darkMode ? 'text-emerald-400' : 'text-emerald-600') : (darkMode ? 'text-red-400' : 'text-red-600')
              }`}>
                {!results.hasGoal ? '—' : results.onTrack ? 'On Track' : `Gap: ${formatCurrency(results.gap)}`}
              </p>
            </div>
          </div>

          {/* Progress bar */}
          <div>
            <div className="flex justify-between mb-1">
              <span className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                Progress to {formatCurrency(results.neededAtRetirement)} goal
              </span>
              <span className={`text-[10px] font-semibold ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}>
                {results.progress.toFixed(0)}%
              </span>
            </div>
            <ProgressBar value={results.progress} max={100} height="sm" />
          </div>
        </div>
        <p className={`text-[10px] ${darkMode ? 'text-slate-600' : 'text-slate-400'}`}>
          Estimates only. Not financial advice. Actual results will vary.
        </p>
      </div>
    </Card>
  );
};
